import { Breadcrumbs } from '@warp-ds/react';
import { type PropsWithChildren, useEffect } from 'react';
import { SimpleHead } from 'shared/page/Head';
import { HeroBanner } from 'shared/page/HeroBanner';
import { MainStatic } from 'shared/page/Main';
import { Menu } from 'shared/page/Menu';
import type { SeoData } from 'shared/seo/interfaces';
import type { LevelFactory } from '@/components/factory';
import type { Vertical } from '@/data/verticals';
import { createHeading } from '@/utils/heading';
import { trackDestinationPageView } from '@/utils/tracking';
import styles from './Page.module.css';
import { StructuredData } from './StructuredData';

interface Props {
    factory: LevelFactory;
    vertical: Vertical;
    data: SeoData;
}

export const DestinationPage = ({ factory, vertical, data, children }: PropsWithChildren<Props>) => {
    useEffect(() => {
        trackDestinationPageView(vertical);
    }, []);

    const {
        pagePath,
        destination: { display: destination, image, price },
    } = factory.pageValues();
    const canonical = `https://www.finn.no${data.basePath}${pagePath}`;
    const imageUrl = `${data.cdnPath}${image}.jpg`;
    const displayPrice = price > 0 ? `fra ${price} kr` : '';

    const insertDestination = (str: string): string => str.replaceAll('$destination', destination);

    const siteTitle = insertDestination(data.lang.metaTitleDestination).replace(/\$price/gm, displayPrice);
    const description = insertDestination(data.lang.metaDescriptionDestination);

    const breadcrumbList = [
        <a href="/reise/" key="breadcrumb1">
            Reise
        </a>,
        <a href={data.basePath} key="breadcrumb2">
            {data.lang.title}
        </a>,
        ...factory.breadcrumbList(),
    ];

    const { shortText, SearchComponent } = factory.menuValues();

    return (
        <>
            <SimpleHead
                title={siteTitle}
                canonical={canonical}
                description={description}
                imageUrl={imageUrl.replace('480x360c', 'watermark-social')}
            />
            <MainStatic className={styles['seo-container']}>
                <h1 className="sr-only">{createHeading(vertical, shortText)}</h1>
                <Breadcrumbs className={`${styles['seo-wrapper']} hidden md:flex my-8`}>{breadcrumbList}</Breadcrumbs>
                <div className={styles['seo-menu-wrapper']}>
                    <Menu activeVertical={vertical} />
                </div>
                <HeroBanner vertical={vertical}>
                    <SearchComponent />
                </HeroBanner>
                <div className={styles['seo-wrapper']}>
                    <section className="offscreen">{children}</section>
                </div>
            </MainStatic>
            <StructuredData factory={factory} title={data.lang.title} prefix={data.lang.headingToDestination} path={data.basePath} />
        </>
    );
};
